<template>
  <div class="folder-container" ref="container">
    <div class="gradient-fade left"></div>
    <div class="gradient-fade right"></div>
    <div
      v-for="(folder, index) in folders"
      :key="folder.name"
      class="folder"
      :style="getFolderStyle(index)"
      @click="handleFolderClick(index)"
    >
      <div class="folder-inner" :class="{ active: index === currentCenteredIndex }">
        <img :src="folder.image" :alt="folder.name" />
      </div>
    </div>
    <!-- <div class="folder-caret">
      <svg width="30" height="15" viewBox="0 0 30 15" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M15 0L0 15H30L15 0Z" fill="url(#paint0_linear_12425_9141)"/>
        <defs>
        <linearGradient id="paint0_linear_12425_9141" x1="15" y1="0" x2="15" y2="15" gradientUnits="userSpaceOnUse">
        <stop offset="0.267699" stop-color="#797979"/>
        <stop offset="1" stop-color="#666666"/>
        </linearGradient>
        </defs>
      </svg>
    </div> -->
  </div>
</template>

<script>
export default {
  name: "FolderScroller",
  props: {
    folders: { type: Array, required: true },
  },
  data() {
    return {
      globalOffset: 0, // Global offset in pixels.
      autoScrollSpeed: 0.035, // Pixels per millisecond.
      animationFrameId: null,
      autoScrollPaused: false,
      pauseAfterCenter: 10000, // ms pause after centering.
      containerWidth: 0,
      centeringAnimation: {
        active: false,
        startTime: 0,
        duration: 500, // ms.
        startOffset: 0,
        delta: 0,
      },
      // Track the active folder index.
      currentCenteredIndex: null,
    };
  },
  computed: {
    // Total width of one full cycle.
    totalWidth() {
      return this.folders.length * this.currentSpacing;
    },
    currentSpacing() {
      return window.innerWidth <= 768 ? 120 : 210; // Adjust spacing for mobile
    },
  },
  mounted() {
    this.containerWidth = this.$refs.container.offsetWidth;
    window.addEventListener('resize', this.updateContainerWidth);
    this.startAnimationLoop();
  },
  beforeUnmount() {
    cancelAnimationFrame(this.animationFrameId);
  },
  methods: {
    updateContainerWidth() {
      this.containerWidth = this.$refs.container.offsetWidth;
    },
    startAnimationLoop() {
      let lastTime = performance.now();
      const animate = (currentTime) => {
        const deltaTime = currentTime - lastTime;
        lastTime = currentTime;
        if (this.centeringAnimation.active) {
          // Animate globalOffset from start to target value.
          const elapsed = currentTime - this.centeringAnimation.startTime;
          let progress = Math.min(elapsed / this.centeringAnimation.duration, 1);
          // Ease-out quadratic easing.
          let easing = 1 - Math.pow(1 - progress, 2);
          this.globalOffset =
            this.centeringAnimation.startOffset +
            this.centeringAnimation.delta * easing;
          if (progress === 1) {
            this.centeringAnimation.active = false;
            // After centering, pause auto-scroll briefly.
            this.autoScrollPaused = true;
            setTimeout(() => {
              this.autoScrollPaused = false;
            }, this.pauseAfterCenter);
          }
        } else if (!this.autoScrollPaused) {
          // Auto-scroll: increment globalOffset.
          this.globalOffset += this.autoScrollSpeed * deltaTime;
        }
        // Check which folder is closest to center and update active index.
        this.checkCenteredFolder();
        this.animationFrameId = requestAnimationFrame(animate);
      };
      this.animationFrameId = requestAnimationFrame(animate);
    },
    // Compute a folder's x position.
    getFolderX(index) {
      let offsetMod = this.globalOffset % this.totalWidth;
      if (offsetMod < 0) offsetMod += this.totalWidth;
      let x = index * this.currentSpacing - offsetMod;
      if (x < -this.currentSpacing) {
        x += this.totalWidth;
      }
      if (x > this.containerWidth) {
        x -= this.totalWidth;
      }
      return x;
    },
    getFolderStyle(index) {
      return {
        position: "absolute",
        left: `${this.getFolderX(index)}px`,
      };
    },
    // Find the folder closest to center and update currentCenteredIndex.
    checkCenteredFolder() {
      let closestIndex = null;
      let closestDist = Infinity;
      const containerCenter = this.containerWidth / 2;
      for (let i = 0; i < this.folders.length; i++) {
        const x = this.getFolderX(i);
        const folderCenter = x + 75; // 75 = half of folder width.
        const dist = Math.abs(folderCenter - containerCenter);
        if (dist < closestDist) {
          closestDist = dist;
          closestIndex = i;
        }
      }
      // Update active index if changed.
      if (closestIndex !== this.currentCenteredIndex) {
        this.currentCenteredIndex = closestIndex;
        this.$emit("folder-selected", closestIndex);
      }
    },
    handleFolderClick(index) {
      if (this.centeringAnimation.active) return;
      this.autoScrollPaused = true;
      const targetX = this.containerWidth / 2 - 75;
      const idealOffset = index * this.currentSpacing - targetX;
      let k = Math.round((this.globalOffset - idealOffset) / this.totalWidth);
      const targetGlobalOffset = idealOffset + k * this.totalWidth;
      this.centeringAnimation = {
        active: true,
        startTime: performance.now(),
        duration: 500,
        startOffset: this.globalOffset,
        delta: targetGlobalOffset - this.globalOffset,
      };
      // Emit event on click.
      this.$emit("folder-selected", index);
    },
  },
};
</script>

<style scoped>
.folder-container {
  position: relative;
  overflow: hidden;
  padding-top: 3rem;
  height: 140px;
}
.folder {
  position: absolute;
  -webkit-user-select: none; /* Safari */
  -ms-user-select: none; /* IE 10 and IE 11 */
  user-select: none; /* Standard syntax */
  width: 120px;
  height: auto;
  padding: 0 2rem;
}

.folder img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.folder-inner {
  transition: transform 0.3s ease, opacity 0.3s ease;
  transform: scale(1);
  opacity: 0.5;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  cursor: pointer;
}
/* The active folder gets scale 1.2 and full opacity */
.folder-inner.active {
  transform: scale(1.2);
  opacity: 1;
}
/* On hover, non-active folders scale to 1.1 with full opacity */
.folder-inner:not(.active):hover {
  transform: scale(1.2);
  /* opacity: 1; */
}
/* On press */
.folder-inner:active {
  transform: scale(0.8);
}

.folder-caret {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  position: absolute;
  bottom: 0;
}

.folder-caret svg {
  height: 100%;
}

.gradient-fade {
  position: absolute;
  top: 0;
  width: 24px;
  height: 100%;
  pointer-events: none;
  z-index: 1;
}

.gradient-fade.left {
  left: 0;
  background: linear-gradient(to right, var(--container-color), transparent);
}

.gradient-fade.right {
  right: 0;
  background: linear-gradient(to left, var(--container-color), transparent);
}

@media (max-width: 768px) {

  .folder-container {
    height: 98px;
  }
  .folder {
    width: 80px;
  }
  .folder-caret svg {
    width: 22px;
    height: auto;
    bottom: 0;  
  }
}
</style>
